/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT:
        1) Do not import from '/ezlibrary/exceptions/EzExceptions.js' to avoid circular dependencies.
        2) Do not import from '/ezlibrary/enums/EzEnumerations.js' to avoid circular dependencies.
        3) Do not import from '/ezlibrary/helpers/EzHelpers.j' to avoid circular dependencies.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzBadParamException }  from '/ezlibrary/exceptions/EzBadParamException.js';
import { EzBadStateException }  from '/ezlibrary/exceptions/EzBadStateException.js';

import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';

import { EzJSONSerializable } from '/ezlibrary/entities/EzJSONSerializable.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';

import { EzInstanceState } from '/ezlibrary/EzInstanceState.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES:
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Extending from EzClass Example/Template:
        1) Copy the template: /src/main/webapp/templates/EzSingletonClassTemplate.js
        2) Rename the file to your class name
        3) Search + Replace matching case 'EzMyClass' with your class name
        4) Search + release matching case 'ezMyClass' with your class's ezApi name.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @description
 * Base class for ezClocker Website Javascript Classes
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * WARNING:
 *     1) Do not import /ezlibrary/exceptions/EzExceptions.js into this class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzClass } from '/ezlibrary/EzClass.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with other ezClocker JS classes:
 *      import {
 *         EzClass,
 *         ... other supported classes ...
 *     } from ''/ezlibrary/ezclocker-classes/EzClasses.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzClass extends EzJSONSerializable {
    /**
     * @public @static @readonly @property
     * Gets the EzApi ready event name.
     *
     * @returns {string}
     */
    static get ezOnEzApiReadyEventName() {
        return 'onEzApiReady';
    }

    /**
     * @public @static @readonly @property
     * Gets the global reference of ezApi from globalThis.ezApi
     *
     * @returns {null|EzApi}
     */
    static get ezApiInstance() {
        return EzObject.assignOrNull(globalThis?.ezApi);
    }

    /**
     * @static
     * @public @function
     * Generic function to register the provided clazzToRegisterWithEzApi with EzApi.
     * @param {object} clazzToRegisterWithEzApi
     * Class to register with EzApi
     * @returns {boolean}
     * True if registered, false if unable to register at this time.
     */
    static ezRegisterClazzWithEzApi(clazzToRegisterWithEzApi) {
        if (!EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezRegisterClazzWithEzApi);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezCanRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezCanRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezCanRegisterWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezIsRegisteredWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezIsRegisteredWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezCanRegisterWithEzApi) &&
            !EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezIsRegisteredWithEzApi)) {
            globalThis.ezApi.ezRegisterNewApi(clazzToRegisterWithEzApi, clazzToRegisterWithEzApi.ezApiName);
        }

        return EzRegistrationState.REGISTERED === clazzToRegisterWithEzApi.ezApiRegistrationState;
    }

    /**
     * @static
     * @public @method
     * Adds a document event listener for the provided readyEventName that calls the provided
     * ezRegistrator function.
     * @param {string} readyEventName
     * @param {function} registratorCallback
     */
    static ezWaitReady(readyEventName, registratorCallback) {
        if (!EzString.hasText(readyEventName)) {
            throw new EzBadParamException(
                'readyEventName',
                EzClass,
                EzClass.ezWaitReady);
        }
        if (!EzFunction.isFunction(registratorCallback)) {
            throw new EzBadParamException(
                'registratorCallback',
                EzClass,
                EzClass.ezWaitReady);
        }

        globalThis.document.addEventListener(
            readyEventName,
            registratorCallback);
    }

    /**
     * @static
     * @public @method
     * Creates wait events for all the dependency classes in the provided dependencyClasses array
     * that have ezEventNames.onReady event name that call the provided ezApiRegistrationmethod.
     * @param {array} ezApiDependencyClazzes
     * Default: EzArray.EMPTY
     * @param {function} ezApiRegistrationFunction
     * Default: this.ezRegisterWithEzApi()
     * Function to call to register this class with EzApi if all the provided ezApiDependencyClazzes indicate they are ready.
     */
    static ezWaitDependenciesReady(ezApiDependencyClazzes = EzArray.EMPTY, ezApiRegistrationFunction = null) {
        /*
        if (EzObject.isUndefined(this?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting ${this.name} to have static property 'ezApiRegistrationState'`,
                `However, ${this.name}.ezApiRegistrationState is undefined`);
        }
        if (EzObject.isUndefined(this?.ezApiName)) {
            throw new EzBadStateException(
                `Expected static property ${this.name}.ezApiName to return a non-null, non-empty string value.`,
                `However, static property ${this.name}.ezApiName is undefined`);
        }
        */
        if (!EzFunction.isFunction(ezApiRegistrationFunction)) {
            ezApiRegistrationFunction = this.ezRegisterClazzWithEzApi();
        }

        if (ezApiRegistrationFunction()) {
            globalThis?.console?.debug(`Singleton instance ${this.name} is already registered with EzApi.`);

            return;
        }

        EzClass.ezWaitReady(
            EzClass.ezOnEzApiReadyEventName,
            ezApiRegistrationFunction);

        if (!EzArray.hasLength(ezApiDependencyClazzes)) {
            globalThis?.console?.debug(`Singleton instance ${this.name} does not mot have any EzApi dependencies to wait for.`);

            return;
        }

        for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
            if (EzString.hasLength(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                EzClass.ezWaitReady(
                    ezApiDependencyClazz.ezEventNames.onReady,
                    ezApiRegistrationFunction);
            }
        }
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @function
     * Wait for all required EzApi dependencies to be ready before registering this class with EzApi.
     * @param {object} clazzToRegisterWithEzApi
     * Class extended from EzClass that will register with EzApi
     * @returns {string}
     * A valid enum property value fomr EzRegistratioNState
     * Returns the provided clazzToRegisterWithEzApi's ezApiRegistrationState
     */
    static ezWaitRequiredEzApiDependenciesReady(clazzToRegisterWithEzApi) {
        if (!EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezWaitRequiredEzApiDependenciesReady);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezIsRegisteredWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezIsRegisteredWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiName)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiName'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiName is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static function named 'ezRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRegisterWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRequiredEzApiDependencies)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezRequiredEzApiDependencies'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRequiredEzApiDependencies is undefined`);
        }

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            // Already registered
            globalThis?.console?.debug(`Singleton instance for ezApiName=${clazzToRegisterWithEzApi.ezApiName} is already registerd with EzApi.`);

            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        clazzToRegisterWithEzApi.ezApiRegistrationState = EzRegistrationState.PENDING;

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezRegisterWithEzApi())) {
            globalThis?.console?.debug(`Singleton instance for ezApiName=${clazzToRegisterWithEzApi.ezApiName} successfully registerd with EzApi.`);

            // Registered with success!
            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        // Register upon EzApi.onEzApiReady revent
        EzClass.ezWaitReady(
            EzClass.ezOnEzApiReadyEventName,
            clazzToRegisterWithEzApi.ezRegisterWithEzApi);

        if (!EzArray.hasLength(clazzToRegisterWithEzApi.ezRequiredEzApiDependencies)) {
            globalThis?.console?.debug(`Singleton instance for ezApiName=${clazzToRegisterWithEzApi.ezApiName} does not have any required EzApi dependencies.`);

            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        // Wait for ezApi registration of the dependency classes
        for (let ezApiDependencyClazz of clazzToRegisterWithEzApi.ezRequiredEzApiDependencies) {
            if (EzObject.isValid(ezApiDependencyClazz) && EzString.hasLength(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                EzClass.ezWaitReady(
                    ezApiDependencyClazz.ezEventNames.onReady,
                    clazzToRegisterWithEzApi.ezRegisterWithEzApi);
            }
        }

        return clazzToRegisterWithEzApi.ezApiRegistrationState;

        /*
        return clazzToRegisterWithEzApi.ezWaitDependenciesReadyIfNotAlreadyRegistered(
            clazzToRegisterWithEzApi,
            clazzToRegisterWithEzApi.ezRequiredEzApiDependencies);
        */
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @method
     * Creates wait events for all the dependency classes in the provided dependencyClasses array
     * that have ezEventNames.onReady event name that call the provided ezApiRegistrationmethod.
     * @param {object} clazzToRegisterWithEzApi
     * Class extending EzClass that needs to wait for dependencies to be ready before registering with ezApi.
     * @param {array} ezApiDependencyClasses
     * Default: EzArray.EMPTY
     * @returns {string}
     * A valid enum property value fomr EzRegistratioNState
     * Returns the provided clazzToRegisterWithEzApi's ezApiRegistrationState
     */
    static ezWaitDependenciesReadyIfNotAlreadyRegistered(clazzToRegisterWithEzApi = null, ezApiDependencyClazzes = EzArray.EMPTY) {
        if (EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw new EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezWaitDependenciesReadyIfNotAlreadyRegistered);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezIsRegisteredWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezIsRegisteredWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static function named 'ezRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRegisterWithEzApi is undefined`);
        }

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            // Already registered
            globalThis?.console?.debug(`Class ${clazzToRegisterWithEzApi.name} is already registerd with EzApi.`);

            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        clazzToRegisterWithEzApi.ezApiRegistrationState = EzRegistrationState.PENDING;

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezRegisterWithEzApi())) {
            globalThis?.console?.debug(`Class ${clazzToRegisterWithEzApi.name} successfully registerd with EzApi.`);

            // Registered with success!
            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        // Register upon EzApi.onEzApiReady revent
        EzClass.ezWaitReady(
            EzClass.ezOnEzApiReadyEventName,
            clazzToRegisterWithEzApi.ezRegisterWithEzApi);

        if (EzArray.hasLength(ezApiDependencyClazzes)) {
            // Wait for ezApi registration of the dependency classes
            for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
                if (EzString.hasLength(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                    EzClass.ezWaitReady(
                        ezApiDependencyClazz.ezEventNames.onReady,
                        clazzToRegisterWithEzApi.ezRegisterWithEzApi);
                }
            }
        }

        return clazzToRegisterWithEzApi.ezApiRegistrationState;
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @method
     * Determines if:
     * 1) clazzToRegisterWithEzApi.ezApiRegistrationState equals EzRegistrationState.PENDING
     * 2) EzBoolean.booleanOrfalse(gloalThis?.ezApi?.ready) is true
     * 3) The ready property of all the clazzToRegisterWithEzApi.requiredDependencyEzApiNames (if ant) equals true
     * @param {object} clazzToRegisterWithEzApi
     * Clas extended from EzClass that will register with EzApi
     * @returns {boolean}
     * Returns true if all required EzApi dependencies are ready, false otherweise.
     */
    static ezAreAllRequiredEzApiDependenciesReady(clazzToRegisterWithEzApi) {
        if (EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw new EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezAreAllRequiredEzApiDependenciesReady);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegistered)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezRequiredEzApiDependencies'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRequiredEzApiDependencies is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static function named 'ezRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRegisterWithEzApi is undefined`);
        }

        let allEzApiDependenciesReady = EzRegistrationState.PENDING === clazzToRegisterWithEzApi.ezApiRegistrationState &&
            EzBoolean.booleanOrFalse(globalThis?.ezApi?.ready);

        if (EzBoolean.isFalse(allEzApiDependenciesReady) || !EzArray.hasLength(clazzToRegisterWithEzApi?.ezRequiredEzApiDependencies)) {
            return allEzApiDependenciesReady;
        }

        for (let index = 0; index < clazzToRegisterWithEzApi.ezRequiredEzApiDependencies.length; index++) {
            const requiredEzApiDepdnencyNameOrClass = clazzToRegisterWithEzApi.ezRequiredEzApiDependencies[index];

            if (EzObject.isValid(requiredEzApiDepdnencyNameOrClass)) {
                allEzApiDependenciesReady = EzString.isString(requiredEzApiDepdnencyNameOrClass)
                    ? allEzApiDependenciesReady && EzBoolean.booleanOrFalse(
                        globalThis?.ezApi?.ezclocker?.[requiredEzApiDepdnencyNameOrClass?.ezApiName]?.ready)
                    : allEzApiDependenciesReady && EzBoolean.booleanOrFalse(
                        globalThis?.ezApi?.ezclocker?.[requiredEzApiDepdnencyNameOrClass]?.ready);
            }

            if (EzBoolean.isFalse(allEzApiDependenciesReady)) {
                break;
            }
        }

        return allEzApiDependenciesReady;

        /*
        return clazzToRegisterWithEzApi.ezCheckIfAllRequiredEzApiDependenciesReady(
            clazzToRegisterWithEzApi.ezApiRegistrationState,
            clazzToRegisterWithEzApi.ezRequiredEzApiDependencies);
        */
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @method
     * Determines if:
     * 1) The provided currentRegistrationState equals EzRegistrationState.PENDING
     * 2) EzBoolean.booleanOrfalse(gloalThis?.ezApi?.ready) is true
     * 3) The ready property of all the EzApi names in the requiredDependencyEzApiNames is true
     * @param {string}
     * A valid enum property value from EzRegistrationState
     * @param {array} currentRegistrationState
     * Default: EzArray.EMPTY
     * Array of string (EzApi registration names) or array of classes that will register with EzApi.
     * @returns {boolean}
     */
    static ezCheckIfAllRequiredEzApiDependenciesReady(currentRegistrationState = EzRegistrationState.UNKNOWN, requiredDependencyEzApiNames = EzArray.EMPTY) {
        let allDepsReady = EzRegistrationState.PENDING === currentRegistrationState && EzBoolean.booleanOrFalse(globalThis?.ezApi?.ready);

        if (EzBoolean.isFalse(allDepsReady) || !EzArray.hasLength(requiredDependencyEzApiNames)) {
            return allDepsReady;
        }

        for (let index = 0; index < requiredDependencyEzApiNames.length; index++) {
            const requiredDependencyEzApiNameOrClass = requiredDependencyEzApiNames[index];

            if (EzObject.isValid(requiredDependencyEzApiNameOrClass)) {
                allDepsReady = EzString.isString(requiredDependencyEzApiNameOrClass)
                    ? allDepsReady && EzBoolean.booleanOrFalse(
                        globalThis?.ezApi?.ezclocker?.[requiredDependencyEzApiNameOrClass?.ezApiName]?.ready)
                    : allDepsReady && EzBoolean.booleanOrFalse(
                        globalThis?.ezApi?.ezclocker?.[requiredDependencyEzApiNameOrClass]?.ready);
            }

            if (EzBoolean.isFalse(allDepsReady)) {
                break;
            }
        }

        return allDepsReady;
    }

    /**
     * @public @constructor
     * Creates a new instance of this class.
     */
    constructor() {
        super();

        this.ezStates.push(EzInstanceState.CONSTRUCTED);
    }

    /**
     * @public @field
     * Stores the ready state of this instance.
     *
     * @type {boolean}
     */
    #ready = false;
    /**
     * @public @readonly @property
     * Gets if the EzApi singleton instance is registered with EzApi.
     *
     * @returns {boolean}
     */
    get ready() {
        /*return undefined !== globalThis?.ezApi?.ezclocker[this?.ezApiName] &&
            null != globalThis?.ezApi?.ezclocker[this?.ezApiName];*/
        return this.#ready;
    }
    /**
     * TODO: Remove after all attempts to set the ready property get removed.
     * @public @setter @property
     * Setter for the ready property is no longer useable. Ready property is now calculated.
     *
     * @param {boolean} _isReady
     *
     * @deprecated
     * Removing in a future release.
     */
    set ready(_isReady) {
        // Setter is now ignored
        this.#ready = EzBoolean.booleanOrFalse(_isReady);
    }

    /**
     * @public @field
     * Stores the EzInstanceState values for the instance.
     *
     * @type {array}
     */
    #ezStates = [];
    /**
     * @public @setter @property
     * Gets the EzInstanceState values for the instance.
     *
     * @returns {array}
     */
    get ezStates() {
        return this.#ezStates;
    }
    /**
     * @public @setter @property
     * Sets the EzInstanceState values for the instance.
     *
     * @param {array} states
     * Default: EzArray.EMPTY;
     */
    set ezStates(states = EzArray.EMPTY) {
        this.#ezStates = EzArray.arrayOrEmpty(states);

        globalThis?.console?.debug(
            `[Enumeration ${this.constructor.name}: ezStates Updated (${JSON.stringify(this.#ezStates)})]`);
    }

    /**
     * @override
     * @public @readonly @property
     * Gets this instance as a JSON Object (will no longer have the getter/setter methods).
     * Usefull for serailization to JSON.
     *
     * @returns {object}
     */
    get asJSONObject() {
        return {
            ready: this.ready,
            ezStates: this.ezStates
        };
    }

    /**
     * @override
     * @public @readonly @property
     * Gets this instance as JSON string (no formatting)
     *
     * @returns {String}
     */
    get asJSON() {
        return super.asJSON;
    }

    /**
     * @public @method
     * Initializes this instance.
     *
     * @returns {EzClass|object}
     */
    ezInit() {
        return this;
    }

    /**
     * @override
     * @public @method
     * Converts this instance into a JSON string with optional formatting.
     *
     * @param {undefined|null|Number} indentValue
     * @param {undefined|null|Boolean} htmlDisplay
     *
     * @returns {String}
     */
    ezToJSON(indentValue, htmlDisplay) {
        return super.ezToJSON(indentValue, htmlDisplay);
    }

    /**
     * @override
     * @public @method
     * Copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
     *
     * @param {Object} jsonObject
     *
     * @returns {Object}
     * Returns this instance with the key & values from the provided jsonObject.
     */
    ezFromJSONObject(jsonObject) {
        return super.ezFromJSONObject(jsonObject);
    }

    /**
     * @override
     * @public @method
     * Converts the provided jsonString to a JSON object and then
     * passes that object to ezFromJSONObject() to copies properties to this instance
     * (even if this instance does not define the property)
     *
     * @param {String} jsonString
     *
     * @returns {Object}
     * Returns this instance with the key & values from the provided json string.
    */
    ezLoadFromJson(jsonString) {
        return super.ezLoadFromJson(jsonString);
    }
}

// /*
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * EzApi Registrating Class Extended from EzClass Template
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * TEMPLATE NOTES:
//  * Avoid implementing the constructor as only the default constructor
//  * (without params) is supported and assumed by EzApi during registration.
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  */
//
// import {
//     EzBadParamException
// } from '/ezlibrary/exceptions/EzExceptions.js';
//
// import {
//     EzObject,
//     EzBoolean,
//     EzNumber,
//     EzString,
//     EzArray,
//     EzFunction,
//     EzHtml,
//     EzJson,
//     EzPromise
// } from '/ezlibrary/helpers/EzHelpers.js';
//
// import {
//     EzRegistrationState,
//     EzContextEventName,
//     EzElementEventName
// } from '/ezlibrary/enums/EzEnumerations.js';
//
// /**
//  * @class
//  * @extends {EzClass}
//  * @description
//  * Description of what this class is for/does
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Import with:
//  *      import { EzMyEzApiRegistratedClass } from '/path/EzMyEzApiRegistratedClass.js';
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Singleton instance ready when true:
//  *      globalThis?.ezApi?.ezclocker?.[EzMyEzApiRegistratedClass.ezApiName]?.ready
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Listen for singleton instance ready event:
//  *  Option #1:
//  *      EzClass.ezWaitReady(
//  *          EzMyEzApiRegistratedClass.ezOnReady,
//  *          {listening_class}.#ezRegistrator);
//  *  Option #2:
//  *      {listening_class}.ezWaitDependenciesReady(
//  *          [
//  *              // ... other dependencies ...,
//  *              EzMyEzApiRegistratedClass
//  *          ],
//  *          {listening_class}.#ezRegistrator);
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Singleton instance access:
//  *  1) From within this class...: EzMyEzApiRegistratedClass.ezInstance
//  *  2) From outside this class..: globalThis.ezApi.ezclocker.ezMyEzApiRegistratedClass
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  */
// export class EzMyEzApiRegistratedClass extends EzClass {
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Static fields, properties, and methods section.
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * TEMPLATE NOTES:
//     * 1) Place static fields, properties and methods at the top.
//     * 2) Remove this entire comment when implementing the class
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    // Example: static private field with getter/setter (remove this example when implementing the class)
//    // /**
//    //  * @private @field
//    //  * Stores the static field data
//    //  * Initial: 'MyGlobalOperationName'
//    //  * @type {string}
//    //  */
//    // @static #ezGlobalOperationName = 'MyGlobalOperationName';
//    // /**
//    //  * @public @property @getter
//    //  * Gets the static field data
//    //  * @returns {string}
//    //  */
//    // @static get ezGlobalOperationName() {
//    //     return this.#ezGlobalOperationName;
//    // }
//    // /**
//    //  * @public @property @setter
//    //  * Sets the static field data
//    //  * @param {string}
//    //  * Default: 'MyGlobalOperationName'
//    //  */
//    // @static set ezGlobalOperationName(globalOperationName = 'MyGlobalOperationName') {
//    //     this.#ezGlobalOperationName = EzString.stringWithLengthOrDefault(globalOperationName, 'MyGlobalOperationName');
//    // }
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Class fields and properties (getters/setters) section.
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * TEMPLATE NOTES:
//     * 1) Place class public, protected and private fields and properties (getters/setters) in this section.
//     * 2) Remove this entire comment when implementing the class
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    // Example: Class private field with getter/setter (remove this example when implementing the class)
//    // /**
//    //  * @private @field
//    //  * Stores if the operation is finished or not.
//    //  * Initial: false
//    //  * @type {boolean}
//    //  */
//    // #ezOperationFinished = false;
//    // /**
//    //  * @public @property @getter
//    //  * Gets the static field data
//    //  * @returns {boolean}
//    //  */
//    // @static get ezOperationFinished() {
//    //     return this.#ezOperationFinished;
//    // }
//    // /**
//    //  * @public @property @setter
//    //  * Sets the static field data
//    //  * @param {boolean} operationFinished
//    //  * Default: false
//    //  */
//    // @static set ezOperationFinished(operationFinished = false) {
//    //     this.#myStaticField = EzString.stringOrEmpty(myStaticField);
//    // }
//
//    /**
//     * @public @method
//     * Initializes this class after EzApi registration.
//     * Method is called automaticlly during EzApi registration.
//     * @returns {object}
//     * Returns the singleton instance of this class.
//     */
//    ezInit() {
//        /*
//         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//         * Class ezInit() function (REQUIRED)
//         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//         * TEMPLATE NOTES:
//         * 1) The ezInit function is required by EzApi to register this class.
//         * 2) Place initialization code to execute once the singletong class is registered with EzApi.
//         * 3) Already return the singleton instance!
//         * 4) Remove this entire comment when implementing the class
//         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//         */
//        return EzMyEzApiRegistratedClass.ezInstance;
//    }
//
//    /**
//     * @public @method
//     * Initializes the UX for this class.
//     */
//    ezInitUx() {
//        EzPromise.asyncAction(
//            (finished) => {
//                /*
//                 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//                 * Optiona class ezInitUx() function (for classes who control or present ux)
//                 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//                 * TEMPLATE NOTES:
//                 * 1) If the class will control or present UX, then place UX initialization code here.
//                 * 2) Remove this function if not needed.
//                 * 3) Remove this entire comment when implementing the class
//                 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//                 */
//
//                return finished();
//            });
//    }
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Class public and protected functions section
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * TEMPLATE NOTES:
//     * 1) Place all class public and protected functions in this section.
//     * 2) Remove this entire comment when implementing the class
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * EzApi Registration Section
//     * !! DO NOT PLACE ANY NON-EZAPI REGISTRATION CODE WITHIN OR AFTER THIS SECTION !!
//     * TEMPLATE NOTES:
//     * 1) This comment (without the TEMPLATE NOTES) is always required!
//     * 2) Remove the TEMPLATE NOTES comments when implementing
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    /**
//     * @static
//     * @public @readonly @property
//     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
//     * @returns {string}
//     */
//    static get ezApiName() {
//        return 'ezMyEzApiRegistratedClass';
//    }
//
//    /**
//     * @public @static @readonly @property
//     * Gets an object of event names that this class may trigger.
//     * @returns {object}
//     */
//    static get ezEventNames() {
//        return {
//            onReady: 'ezOn_EzMyEzApiRegistratedClass_Ready'
//        };
//    }
//
//    /**
//      * @static
//      * @private @field
//      * Stores the singleton instance of this class that was created by and registerd with EzApi.
//      * @type {EzSpinner}
//      */
//    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzMyEzApiRegistratedClass.ezApiName]
//        ? globalThis.ezApi.ezclocker[EzMyEzApiRegistratedClass.ezApiName]
//        : null;
//    /**
//     * @static
//     * @public @property @getter
//     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
//     * @returns {EzSpinner}
//     */
//    static get ezInstance() {
//        return EzMyEzApiRegistratedClass.#ezInstance;
//    }
//    /**
//     * @static
//     * @public @property @setter
//     * Sets the singleton instance of of this class that is registered with EzApi.
//     * @param {EzMyEzApiRegistratedClass} instance
//     * Singleton instance of this class registered with EzApi
//     */
//    static set ezInstance(instance) {
//        if (null != EzMyEzApiRegistratedClass.#ezInstance) {
//            throw new Error('EzMyEzApiRegistratedClass\'s singleton instance is already reigstered with EzApi.');
//        }
//
//        EzMyEzApiRegistratedClass.#ezInstance = instance;
//    }
//
//    /**
//     * @static
//     * @private @field
//     * Stores the EzApi registration state for this class.
//     * Default value is NULL
//     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
//     * @type {string}
//     * A valid enum property value from EzRegistrationState
//     */
//    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzMyEzApiRegistratedClass.ezApiName]
//        ? EzRegistrationState.REGISTERED
//        : null;
//    /**
//     * @static
//     * @public @property @getter
//     * Gets the ezApi registration state of this classes's singleton instance.
//     * @returns {string}
//     * A valid enum property value from EzRegistrationState
//     */
//    static get ezApiRegistrationState() {
//        return EzMyEzApiRegistratedClass.#ezApiRegistrationState;
//    }
//    /**
//     * @static
//     * @public @property @setter
//     * Sets the ezApi registration state of this classes's singleton instance.
//     * @param {string} ezApiRegistrationState
//     * A valid enum property value from EzRegistrationState
//     */
//    static set ezApiRegistrationState(ezApiRegistrationState) {
//        EzMyEzApiRegistratedClass.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
//    }
//    /**
//     * @static
//     * @public @readonly @property
//     * Gets an array of the required EzApi dependencies that need to be ready before this class can register with EzApi
//     * @returns {array}
//     */
//    static get ezRequiredEzApiDependencies() {
//        return [
//            /*
//             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//             * TEMPLATE NOTES:
//             * Place required EzApi dependency class within the array.
//             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//             */
//        ];
//    }
//
//  /**
//   * @static
//   * @private @readonly @property
//   * Gets if all required dependencies for this class report ready.
//   * In otherwords, the require dependency's singleton instance is created
//   * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
//   * @returns {boolean}
//   */
//  static get #ezCanRegister() {
//      return EzMyEzApiRegistratedClass.ezAllRequiredEzApiDependenciesReady(
//          EzMyEzApiRegistratedClass.ezApiRegistrationState,
//          EzMyEzApiRegistratedClass.requiredEzApiDependencies);
//
//      /*
//      return EzRegistrationState.PENDING === EzwEmployerExportTimeSheetDialog.ezApiRegistrationState && globalThis?.ezApi?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzAnalyticsAndMetrics.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzDateTime.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzSpinner.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzClockerContext.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzFeatureToggles.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzUI.ezApiName]?.ready &&
//          globalThis?.ezApi?.ezclocker?.[EzDialog.ezApiName]?.ready;
//        */
//    }
//
//    /**
//     * @static
//     * @public  @readonly @property
//     * Gets if all required dependencies for this class report ready.
//     * In otherwords, the require dependency's singleton instance is created
//     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
//     * @returns {boolean}
//     */
//    static get ezCanRegisterWithEzApi() {
//        return EzMyEzApiRegistratedClass.#ezCanRegister;
//    }
//
//    /**
//     * @static
//     * @private @readonly @property
//     * Gets if this class's singleton instance is registered with ezApi yet.
//     * @returns {boolean}
//     */
//    static get #ezIsRegistered() {
//        return null != EzMyEzApiRegistratedClass.ezInstance &&
//            EzRegistrationState.REGISTERED === EzMyEzApiRegistratedClass.ezApiRegistrationState;
//    }
//
//    /**
//     * @static
//     * @public @readonly @property
//     * Gets if this class's singleton instance is registered with ezApi yet.
//     * @returns {boolean}
//     */
//    static get ezIsRegisteredWithEzApi() {
//        return EzMyEzApiRegistratedClass.#ezIsRegistered;
//    }
//
//    /**
//     * @static
//     * @private @method
//     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
//     * if successful, false otherwise.
//     * @returns {boolean}
//     */
//    static #ezRegistrator() {
//        if (EzMyEzApiRegistratedClass.canRegisterWithEzApi && !EzMyEzApiRegistratedClass.isRegisteredWithEzApi) {
//            globalThis.ezApi.ezRegisterNewApi(
//                EzMyEzApiRegistratedClass,
//                EzMyEzApiRegistratedClass.ezApiName);
//        }
//
//        return EzRegistrationState.REGISTERED === EzMyEzApiRegistratedClass.ezApiRegistrationState;
//    }
//
//    /**
//     * @static
//     * @public @method
//     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
//     * if successful, false otherwise.
//     * @returns {boolean}
//     */
//    static ezRegisterWithEzApi() {
//        return EzMyEzApiRegistratedClass.#ezRegistrator();
//    }
//
//    /**
//     * @static
//     * Static Initialization
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * >> IMPORTANT <<
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Make sure the following properties and methods get defined BEFORE this static initialization section:
//     *     1) Property getter EzMyEzApiRegistratedClass.ezApiName
//     *     2) Property getter EzMyEzApiRegistratedClass.ezEventNames
//     *     3) Property getter EzMyEzApiRegistratedClass.ezInstance
//     *     4) Property setter EzMyEzApiRegistratedClass.ezInstance
//     *     5) Property getter EzMyEzApiRegistratedClass.ezApiRegistrationState
//     *     6) Property setter EzMyEzApiRegistratedClass.ezApiRegistrationState
//     *     7) Property getter EzMyEzApiRegistratedClass.#ezCanRegister
//     *     8) Property getter EzMyEzApiRegistratedClass.#ezIsRegistered
//     *     9) Method EzwEmployerExportTimeSheetDialog.#ezRegistrator()
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//     static {
//         // Option #1:
//         EzwEmployerExportTimeSheetDialog.ezWaitRequiredEzApiDependenciesReady(EzwEmployerExportTimeSheetDialog);
//
//         /* Option #2
//         if (!EzwEmployerExportTimeSheetDialog.isRegisteredWithEzApi) {
//             EzwEmployerExportTimeSheetDialog.ezApiRegistrationState = EzRegistrationState.PENDING;
//
//             EzwEmployerExportTimeSheetDialog.ezWaitDependenciesReady(
//                 EzwEmployerExportTimeSheetDialog.ezRequiredEzApiDependencies,
//                 EzwEmployerExportTimeSheetDialog.registerWithEzApi);
//         }
//         */
//
//         /* Option #3 (legacy)
//         if (!EzwEmployerExportTimeSheetDialog.isRegisteredWithEzApi) {
//             EzwEmployerExportTimeSheetDialog.ezApiRegistrationState = EzRegistrationState.PENDING;
//
//             if (!EzwEmployerExportTimeSheetDialog.#ezRegistrator()) {
//                 EzClass.ezWaitReady(
//                     EzwEmployerExportTimeSheetDialog.ezOnEzApiReadyEventName,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzAnalyticsAndMetrics.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzDateTime.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzEventEngine.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzSpinner.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzClockerContext.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzFeatureToggles.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzNavigation.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzUI.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzDialog.ezEventNames.onReady,
//                     EzwEmployerExportTimeSheetDialog.#ezRegistrator);
//             }
//         }
//         */
//     }
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * End of EzApi Registration Section
//     * !! DO NOT PLACE ANY CODE AFTER THIS SECTION !!
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * TEMPLATE NOTES:
//     * 1) This comment (without the TEMPLATE NOTES) is always required!
//     * 2) Remove the TEMPLATE NOTES comments when implementing
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
// }